import moment, { Moment } from 'moment'

import i18n from 'modules/core/lib/i18n'

import { ScheduleRecordValuesType } from './types'

export const getBackgroundColor = (index: number, days: moment.Moment[]) => {
  return days[index]?.weekday() === 5 || days[index]?.weekday() === 6 ? 'grey100' : 'white'
}

export const gridColumnSpan = (span: number) => {
  return {
    gridColumn: `span ${span}`,
  }
}

export const gridArea = (
  rowStart: number,
  columnStart: number,
  rowSpan: number,
  columnSpan: number
) => {
  return {
    gridArea: `${rowStart} / ${columnStart} / span ${rowSpan} / span ${columnSpan}`,
  }
}

export const sumMinutesForDate = (
  date: moment.Moment,
  records: ScheduleRecordValuesType[]
): number => {
  return records.reduce((total, record) => {
    const start = moment(record.startDate)
    const end = moment(record.endDate)

    if (date.isBetween(start, end, 'day', '[]')) {
      return total + record.dailyMinutes
    }

    return total
  }, 0)
}

const formatSecondsToHM = (seconds: number): string => {
  const positiveSeconds = Math.abs(seconds)
  const hours = Math.floor(+positiveSeconds / 3600)
  const minutes = Math.ceil(Math.floor(+positiveSeconds - hours * 3600) / 60)

  const absText = minutes == 0 ? `${Number(hours)}h` : `${Number(hours)}h ${Number(minutes)}m`
  return seconds < 0 ? `-${absText}` : absText
}

export const cellText = (
  minutes: number,
  title?: string | null,
  units?: 'total' | 'daily' | 'none'
): string => {
  const formatUnits = () => {
    if (units === 'none') {
      return ''
    } else if (units === 'total') {
      return i18n.t('project_management.schedule.units.total')
    } else {
      return i18n.t('project_management.schedule.units.by_day')
    }
  }

  const text = [formatSecondsToHM(minutes * 60), formatUnits(), title ? ` - ${title}` : ''].join('')
  return text
}

export const formatDecimalHours = (hours: number): string => {
  return Number.isInteger(hours) ? hours.toString() : hours.toFixed(2)
}

const splitRecordByWeekDays = (record: ScheduleRecordValuesType) => {
  const weekDayJsToIso = (jsDay: number): number => (jsDay === 0 ? 7 : jsDay)

  const { realStartDate, realEndDate, weekDays } = record

  if (weekDays?.length === 7 || weekDays?.length === 0) {
    return [record]
  }

  const allowed = new Set(weekDays?.map(weekDayJsToIso))

  let currentDate = moment.utc(realStartDate, 'YYYY-MM-DD')
  const end = moment.utc(realEndDate, 'YYYY-MM-DD')

  let segmentStart: Moment | null = null
  const out: ScheduleRecordValuesType[] = []

  while (currentDate.isSameOrBefore(end, 'day')) {
    const isAllowed = allowed.has(currentDate.isoWeekday())

    if (isAllowed) {
      if (!segmentStart) segmentStart = currentDate.clone()
    } else {
      if (segmentStart) {
        const segmentEnd = currentDate.clone().subtract(1, 'day')
        out.push({
          ...record,
          startDate: segmentStart.format('YYYY-MM-DD'),
          endDate: segmentEnd.format('YYYY-MM-DD'),
        })
        segmentStart = null
      }
    }

    currentDate = currentDate.clone().add(1, 'day')
  }

  if (segmentStart) {
    out.push({
      ...record,
      startDate: segmentStart.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    })
  }

  return out
}

export const splitPlannedRecordsByWeekDays = (records: ScheduleRecordValuesType[]) => {
  return records.flatMap((record) => {
    if (record.weekDays?.length === 7 || record.weekDays?.length === 0) {
      return record
    } else {
      return splitRecordByWeekDays(record)
    }
  })
}
